import MiniSearch from 'minisearch'
import { tokenize, tokenizeQuery } from './textNormalize'
import { applyAlgoliaRerank } from './algoliaComparator'
import { setQuestionBodies, setQuestionChoices, questionChoiceBodies } from './documentFields'

// Build memoised MiniSearch indices from Redux-derived collections.
//
// MiniSearch is a small in-memory full-text engine that uses BM25 scoring
// over a configurable per-token analyser. Token-level scoring is a much
// closer behavioural match to Algolia than character-level fuzzy matching,
// which is why it is the only local engine left; the two are compared
// against Algolia at runtime through `searchReporter`.
//
// Each MiniSearch instance is shaped to mirror the corresponding Algolia
// index — `repos`, `folders`, `sets_questions` — so the same UI primitives
// (`IndexHitList` / `SectionHeader` / per-index totals) can render results
// from any of the three engines without branching.
//
// Hit shape is the original Redux record spread + the standard
// `__localOrigin: true` + `objectID` markers used by all local providers,
// plus a `__localEngine: 'minisearch'` discriminator that the UI reads to
// render a distinct badge variant.

// ─── Tokenization & analyser ───────────────────────────────────────────────

// `tokenize` is the shared Unicode-aware tokenizer (diacritic folding + CJK /
// no-space-script segmentation). MiniSearch applies it to BOTH documents and
// queries, so both sides stay symmetric. Because folding already happens
// inside `tokenize`, `processTerm` only needs to drop empty tokens.
function processTerm(term) {
  if (typeof term !== 'string') return false
  return term.length > 0 ? term : false
}

// ─── Search profiles ───────────────────────────────────────────────────────
//
// Two named profiles run over the SAME indices. MiniSearch accepts a full set
// of search options per `search()` call, so the tuned profile costs one extra
// in-memory BM25 pass over data that is already loaded — no second index, no
// extra memory for documents. Both profiles are reported on every query, which
// makes the comparison paired: same query, same library, same user, same
// moment. See SEARCH_QUALITY_ANALYTICS.md Q21.
// BASELINE is the untouched control arm; TUNED is the tuning line under test.
//
// A third profile, TUNED_BM25, ran `bm25.b` at 0.3 against TUNED's 0.7 for 6.5
// days and returned a clean null: 98.4% of result sets were identical, and the
// per-index split showed exactly 0.000 movement on the title-only indices
// (`repos`, `folders`) with −0.004 on `sets_questions`. The mechanism worked and
// the hypothesis was false, so the lane is retired rather than left running —
// it cost a fourth of the report payload against a hard 65,535-byte ceiling.
// Its data stays in Redshift under the `tuned-v4` version label.
export const SEARCH_PROFILES = {
  BASELINE: 'baseline',
  TUNED: 'tuned',
  TUNED_RERANK: 'tunedRerank',
}

// Bump whenever a profile's option values change. Reported with every quality
// report so rows recorded under different tuning iterations can be told apart in
// Redshift — otherwise a second tuning pass silently pools its results with the
// first one's. Every card in §4e–4g of SEARCH_QUALITY_ANALYTICS.md groups on it.
export const TUNED_PROFILE_VERSIONS = {
  [SEARCH_PROFILES.TUNED]: 'tuned-v3',
  [SEARCH_PROFILES.TUNED_RERANK]: 'tuned-v5',
}

// Non-global so `.test()` stays stateless.
const HAS_DIGIT = /\d/

// ─── Baseline profile (unchanged behaviour) ────────────────────────────────
//
// `prefix: true`  — described in the original code as the Algolia `prefixLast`
//                   equivalent, but it is not: Algolia prefix-matches only the
//                   LAST token, this matches every token. Telemetry shows the
//                   cost — a single-character token prefix-matches most of the
//                   library, which is how `M` returned 42 distinct titles.
// `fuzzy: 0.2`    — Levenshtein tolerance ~20% of token length, applied to
//                   numeric tokens too. `round(0.2 * 4) = 1` edit, so `mr17`
//                   matches MR10 / MR14 / MR16 / MR18.
// `combineWith`   — 'AND' so multi-token queries require every token to
//                   match somewhere in the document (Algolia default).
//
// Kept exactly as-is: it is the control arm of the experiment.
const BASELINE_SEARCH_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  combineWith: 'AND',
}

// ─── Tuned profile ─────────────────────────────────────────────────────────
//
// Mirrors Algolia's actual typo configuration rather than the baseline's
// approximation of it: `minWordSizefor1Typo: 4`, `minWordSizefor2Typos: 8`,
// prefix on the last token only, and no typo tolerance on tokens containing
// digits (Algolia treats numbers as exact by default).
//
// `weights` lowers the score contribution of fuzzy and prefix matches relative
// to exact ones (MiniSearch defaults are 0.45 / 0.375), so an exact match on a
// less-boosted field still outranks an approximate match on a boosted one.
//
// ── v2, from three days of tuned-v1 telemetry ──
//
// `prefix` used to carry a `term.length >= 3` floor as well. Algolia's
// `prefixLast` has no minimum token length, so the floor made this profile
// stricter than the engine the comment above claims to mirror, and Q30 sized
// the cost: on one-character queries the tuned arm returned nothing at all on
// 43.7% of the queries where Algolia found something, against baseline's 6.8%;
// at two characters 41.1% against 0.7%. The two arms converge from three
// characters up — exactly where the floor stopped applying. Removed.
//
// `index === terms.length - 1` stays: that is the part that actually mirrors
// `prefixLast`, and it is what fixed the baseline's `M` → 42-distinct-titles
// behaviour. `!HAS_DIGIT` stays too — it belongs to the digit knob, which is
// the one change v1 got right (excess per query 3.10 → 1.23, precision +0.056).
const TUNED_SEARCH_OPTIONS = {
  combineWith: 'AND',
  prefix: (term, index, terms) => (
    index === terms.length - 1 && !HAS_DIGIT.test(term)
  ),
  fuzzy: (term) => {
    if (HAS_DIGIT.test(term)) return false
    if (term.length >= 8) return 0.25
    if (term.length >= 4) return 0.2
    return false
  },
  weights: { fuzzy: 0.2, prefix: 0.3 },
  // Query side only — documents keep their n-grams. See tokenizeQuery.
  tokenize: tokenizeQuery,
}

// ─── Tuned + BM25 profile (tuned-v4) ───────────────────────────────────────
//
// The re-rank profile matches EXACTLY like TUNED — same prefix predicate, same
// fuzzy ladder, same boosts, same score floor. The only difference is what
// happens after matching: results are re-ordered by an Algolia-style
// lexicographic comparator instead of being left in BM25 score order.
//
// Sharing the matcher is what makes the experiment attributable. The candidate
// set going into the sort is identical by construction, so any movement against
// tuned-v3 is ordering and nothing else.
const TUNED_RERANK_SEARCH_OPTIONS = { ...TUNED_SEARCH_OPTIONS }

const TUNED_SCORE_FLOOR_RATIO = 0.25

// ─── Per-domain index builders ─────────────────────────────────────────────
//
// Each builder produces an independent MiniSearch instance with field
// boosts that mirror `TEMP_LOCAL_SEARCH_INDEXES.md`. The `_kind` field
// labels every document so the unified `sets_questions` index can keep
// the set/question discriminator implicit (matches Algolia's behaviour).
// `_original` carries the source Redux record back into the hit; this
// trades memory for code simplicity and avoids a parallel id→record map.

function notArchived(item) {
  return item && !item.archived && !item.parentArchived
}

function buildRepoDocs(repos) {
  return repos.filter(notArchived).map((repo) => {
    const author = repo.author || {}
    return {
      msId: `repo-${repo.id}`,
      name: repo.name,
      description: repo.description,
      authorFirstName: author.firstName,
      authorLastName: author.lastName,
      _kind: 'repo',
      _original: repo,
    }
  })
}

function buildFolderDocs(folders) {
  return folders.filter(notArchived).map((folder) => ({
    msId: `folder-${folder.id}`,
    name: folder.name,
    _kind: 'folder',
    _original: folder,
  }))
}

function buildSetDocs(sets) {
  return sets.filter(notArchived).map((set) => ({
    msId: `set-${set.id}`,
    name: set.name,
    // Derived through documentFields so the comparator can recover exactly this
    // text at query time to locate WHERE a term matched. See documentFields.js.
    questionBodies: setQuestionBodies(set),
    questionChoices: setQuestionChoices(set),
    _kind: 'set',
    _original: set,
  }))
}

function buildQuestionDocs(questions) {
  return questions.filter(notArchived).map((question) => ({
    msId: `question-${question.id}`,
    body: question.body,
    choices: questionChoiceBodies(question),
    _kind: 'question',
    _original: question,
  }))
}

// ─── Per-profile field boosts ──────────────────────────────────────────────
//
// Only `setsQuestions` differs between the profiles, deliberately: it is the
// index carrying nearly all the traffic, and keeping repos/folders identical
// means any movement there cannot be attributed to a boost change.
//
// The baseline weights body text (`questionBodies: 2` against `name: 3`) high
// enough that a word appearing in ANY question of a set matches the set — the
// reason a query for `Proteins` returns "An alpha particle consists of …", and
// why `math`, `name` and `reading` each return 20+ unrelated sets. The tuned
// profile widens the gap between the title and the body fields instead of
// removing body matching, so a title match wins whenever one exists.
//
// ── v2, from three days of tuned-v1 telemetry ──
//
// v1 widened that gap too far: `name: 6` against `questionBodies: 1` puts a
// body-only match at ~1/6 of a title match, under TUNED_SCORE_FLOOR_RATIO, so
// the floor deleted it before it could be ranked. Q31 sized the damage — 93.6%
// of the documents the tuned profile lost that the baseline found (1,719 of
// 1,836, across 995 distinct titles) had titles that never contained the query
// at all. They were reachable only through question text.
//
// v2 keeps a title-over-body preference but at 2:1 rather than 6:1, so a
// body-only match lands at ~0.5 of a title match and clears the 0.25 floor with
// room to spare. Baseline's ratio, for reference, is 1.5:1.
const BASELINE_BOOSTS = {
  repos: {
    name: 3, description: 1, authorFirstName: 1, authorLastName: 1,
  },
  folders: { name: 1 },
  setsQuestions: {
    name: 3, questionBodies: 2, questionChoices: 1, body: 3, choices: 1,
  },
}

const TUNED_BOOSTS = {
  repos: BASELINE_BOOSTS.repos,
  folders: BASELINE_BOOSTS.folders,
  setsQuestions: {
    name: 4, questionBodies: 2, questionChoices: 1, body: 4, choices: 1,
  },
}

const REPOS_OPTIONS = {
  idField: 'msId',
  fields: ['name', 'description', 'authorFirstName', 'authorLastName'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...BASELINE_SEARCH_OPTIONS, boost: BASELINE_BOOSTS.repos },
}

const FOLDERS_OPTIONS = {
  idField: 'msId',
  fields: ['name'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...BASELINE_SEARCH_OPTIONS, boost: BASELINE_BOOSTS.folders },
}

const SETS_QUESTIONS_OPTIONS = {
  idField: 'msId',
  fields: [
    'name', 'questionBodies', 'questionChoices',
    'body', 'choices',
  ],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...BASELINE_SEARCH_OPTIONS, boost: BASELINE_BOOSTS.setsQuestions },
}

// ─── Memoisation cache ─────────────────────────────────────────────────────
//
// Keyed on the input list reference. Redux replaces the array reference
// whenever any item changes; the cache returns the same MiniSearch instance
// until that happens.

const reposCache = new WeakMap()
const foldersCache = new WeakMap()
const setsQuestionsCache = new WeakMap()

function getRepoIndex(repos) {
  const list = Array.isArray(repos) ? repos : []
  const cached = reposCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(REPOS_OPTIONS)
  ms.addAll(buildRepoDocs(list))
  reposCache.set(list, ms)
  return ms
}

function getFolderIndex(folders) {
  const list = Array.isArray(folders) ? folders : []
  const cached = foldersCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(FOLDERS_OPTIONS)
  ms.addAll(buildFolderDocs(list))
  foldersCache.set(list, ms)
  return ms
}

function getSetsQuestionsIndex(sets, questions) {
  const setsList = Array.isArray(sets) ? sets : []
  const questionsList = Array.isArray(questions) ? questions : []
  const cached = setsQuestionsCache.get(setsList)
  if (cached && cached.questions === questionsList) return cached.ms
  const ms = new MiniSearch(SETS_QUESTIONS_OPTIONS)
  ms.addAll([...buildSetDocs(setsList), ...buildQuestionDocs(questionsList)])
  setsQuestionsCache.set(setsList, { questions: questionsList, ms })
  return ms
}

/**
 * Build per-domain MiniSearch indices from Redux collections.
 *
 * @returns {{ repos: MiniSearch, folders: MiniSearch, setsQuestions: MiniSearch }}
 */
export function buildMiniSearchIndices({
  sets, questions, folders, repos,
}) {
  return {
    repos: getRepoIndex(repos),
    folders: getFolderIndex(folders),
    setsQuestions: getSetsQuestionsIndex(sets, questions),
  }
}

/**
 * Per-index document counts for a built index set.
 *
 * Reported alongside every search-quality report. Both local engines only ever
 * index what Redux has loaded, so without this a "missing hit" is ambiguous
 * between *the tokenizer failed to match it* and *the document was never in the
 * local index at all*. Q18 in SEARCH_QUALITY_ANALYTICS.md separates the two.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @returns {{ repos: number, folders: number, sets_questions: number }}
 */
export function miniSearchIndexSizes(indices) {
  const count = (ms) => ((ms && typeof ms.documentCount === 'number') ? ms.documentCount : 0)
  const set = indices || {}
  return {
    repos: count(set.repos),
    folders: count(set.folders),
    // eslint-disable-next-line camelcase
    sets_questions: count(set.setsQuestions),
  }
}

// Result-object index names to the MiniSearch instances they were produced from.
const INDEX_INSTANCE_KEY = {
  repos: 'repos',
  folders: 'folders',
  // eslint-disable-next-line camelcase
  sets_questions: 'setsQuestions',
}

/**
 * Was this document in the local index at all?
 *
 * Index sizes answer the question for a whole collection; this answers it for
 * one document, which is what a single missing hit needs. A document that IS
 * indexed but did not come back is a matching or ranking failure and belongs in
 * the tuning backlog. A document that was never indexed is a loading failure and
 * no amount of tuning will find it.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @param {('repos'|'folders'|'sets_questions')} indexName
 * @param {('repo'|'folder'|'set'|'question'|null)} documentType from getDocumentType
 * @param {string} objectID
 * @returns {boolean|null} null when the document could not be identified, which
 *   must not be read as `false` — an unrecognised record shape is not a missing one
 */
export function miniSearchHasDocument(indices, indexName, documentType, objectID) {
  if (!indices || !documentType || !objectID) return null
  const ms = indices[INDEX_INSTANCE_KEY[indexName]]
  if (!ms || typeof ms.has !== 'function') return null
  return ms.has(`${documentType}-${objectID}`)
}

// ─── Hit projection ────────────────────────────────────────────────────────

function toHit(result, profile) {
  const original = result._original || {}
  return {
    ...original,
    objectID: original.id,
    __localOrigin: true,
    __localEngine: 'minisearch',
    __minisearchOrigin: true,
    __minisearchProfile: profile || SEARCH_PROFILES.BASELINE,
    __minisearchScore: result.score,
    // MiniSearch returns `terms: ['math']` and `match: { math: ['name'] }`.
    // Both are kept on the hit so the search-tuning sandbox can show which
    // query term matched which field.
    __minisearchTerms: result.terms,
    __minisearchMatch: result.match,
  }
}

const NOOP = () => {}

// Per-index hit cap applied when the caller does not pass one. Exported so the
// orchestrator can report the cap actually in force alongside each quality
// report — a row that returned exactly the cap is measuring the cap, not recall.
export const DEFAULT_LIMIT_PER_INDEX = 10

function emptyIndexResult() {
  return {
    hits: [], hitCount: 0, hasMore: false, loadMore: NOOP,
  }
}

// Resolve the search options for one index under one profile. The baseline
// values match the constructor `searchOptions` exactly, so passing them
// explicitly changes nothing — it just puts both profiles on the same footing.
// The tuned arm carries its own boosts; the baseline arm is never touched.
function isTunedProfile(profile) {
  return profile === SEARCH_PROFILES.TUNED || profile === SEARCH_PROFILES.TUNED_RERANK
}

const PROFILE_SEARCH_OPTIONS = {
  [SEARCH_PROFILES.BASELINE]: BASELINE_SEARCH_OPTIONS,
  [SEARCH_PROFILES.TUNED]: TUNED_SEARCH_OPTIONS,
  [SEARCH_PROFILES.TUNED_RERANK]: TUNED_RERANK_SEARCH_OPTIONS,
}

function profileSearchOptions(profile, indexKey) {
  const options = PROFILE_SEARCH_OPTIONS[profile] || BASELINE_SEARCH_OPTIONS
  return {
    ...options,
    boost: (isTunedProfile(profile) ? TUNED_BOOSTS : BASELINE_BOOSTS)[indexKey],
  }
}

// MiniSearch returns results already sorted by descending score, so the top hit
// sets the bar for the rest. Baseline is left untouched — it is the control arm.
//
// v3: the floor only runs when the slice actually binds. It exists to stop a
// long junk tail padding out a fixed-size slice; when there are fewer matches
// than the caller asked for there is no tail to cut. Under v2 it ran
// unconditionally and truncated wide surfaces badly — on sectionHome it reduced
// 200 available slots to 19 returned where Algolia found 47, and 676 of 683
// remaining losses happened nowhere near the cap. See §4g.
function applyScoreFloor(results, profile, limit) {
  if (!isTunedProfile(profile) || results.length === 0) return results
  if (typeof limit !== 'number' || results.length <= limit) return results
  const floor = results[0].score * TUNED_SCORE_FLOOR_RATIO
  const kept = results.filter((r) => r.score >= floor)
  // The floor reorders and trims the tail; it must never leave the caller with
  // fewer hits than it asked for.
  return kept.length >= limit ? kept : results.slice(0, limit)
}

// Re-order the candidate set the way Algolia would, for the re-rank profile
// only. Runs BEFORE the cap is applied, which is the entire mechanism: on a
// capped surface the sort decides which documents survive the slice, so this
// changes membership and not just presentation. See algoliaComparator.js.
function applyRerank(results, query, profile, indexKey) {
  if (profile !== SEARCH_PROFILES.TUNED_RERANK) return results
  return applyAlgoliaRerank(results, query, indexKey)
}

function runIndex(ms, query, limit, profile, indexKey) {
  if (!query || !ms) return emptyIndexResult()
  const found = ms.search(query, profileSearchOptions(profile, indexKey))
  const floored = applyScoreFloor(found, profile, limit)
  const all = applyRerank(floored, query, profile, indexKey)
  const hitCount = all.length
  const capped = (typeof limit === 'number' && limit > 0) ? all.slice(0, limit) : all
  return {
    hits: capped.map((result) => toHit(result, profile)),
    hitCount,
    // Everything is already in memory, so "more" simply means the cap cut the
    // list short. The provider supplies the `loadMore` that raises the cap —
    // this module has no state to page through.
    hasMore: capped.length < hitCount,
    loadMore: NOOP,
  }
}

// `limitPerIndex` is either one number for every index, or a per-index map when
// the caller pages indexes independently (Show more raises one index's cap
// without disturbing the others).
function resolveLimit(limitPerIndex, resultKey) {
  if (limitPerIndex && typeof limitPerIndex === 'object') {
    const limit = limitPerIndex[resultKey]
    return typeof limit === 'number' ? limit : DEFAULT_LIMIT_PER_INDEX
  }
  return limitPerIndex
}

/**
 * Run a query against every MiniSearch index and return per-index results
 * in the same shape as `AlgoliaSearchProvider`'s `indexResults`.
 *
 * `hitCount` is the true uncapped match count while `hits` is capped at
 * `limitPerIndex`.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @param {string} query
 * @param {number|{repos: number, folders: number, sets_questions: number}} [limitPerIndex=10]
 * @param {('baseline'|'tuned')} [profile='baseline']
 */
export function searchPerMiniSearchIndex(
  indices,
  query,
  limitPerIndex = DEFAULT_LIMIT_PER_INDEX,
  profile = SEARCH_PROFILES.BASELINE,
) {
  const limitFor = (resultKey) => resolveLimit(limitPerIndex, resultKey)
  return {
    repos: runIndex(indices.repos, query, limitFor('repos'), profile, 'repos'),
    folders: runIndex(indices.folders, query, limitFor('folders'), profile, 'folders'),
    // eslint-disable-next-line camelcase
    sets_questions: runIndex(
      indices.setsQuestions,
      query,
      limitFor('sets_questions'),
      profile,
      'setsQuestions',
    ),
  }
}
